import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Tooltip } from "antd";
import { COLORS, THICKNESSES, STICKERS } from "./constants";
import { Btn, Sep, GroupLabel } from "./EditorButtons";
import Icon from "./Icon";
import SketchColorPicker from "./SketchColorPicker";

// ── Floating popup that positions itself relative to a trigger element ─────
function FloatingPopup({ anchorRef, children, onClose, width = 200, padding = 14 }) {
  const popupRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRef?.current || !popupRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const pw = popupRect.width;
    const ph = popupRect.height;
    const ww = window.innerWidth;
    const wh = window.innerHeight;

    let left, top;
    if (ww >= 1024) {
      // Desktop: open to the right of the sidebar, aligned with the button vertically
      left = rect.right + 10;
      top = Math.min(rect.top, wh - ph - 10);
      if (top < 10) top = 10;
    } else {
      // Tablet/mobile: open above the bottom bar
      left = Math.min(Math.max(rect.left - pw / 2 + rect.width / 2, 8), ww - pw - 8);
      top = rect.top - ph - 10;
      if (top < 8) top = rect.bottom + 10;
    }
    setPos({ top, left });
  }, [anchorRef, width, children]);

  // Close on outside click
  useEffect(() => {
    function handle(e) {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose?.();
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose, anchorRef]);

  return (
    <div ref={popupRef} style={{
      position: "fixed",
      top: pos?.top || 0,
      left: pos?.left || 0,
      zIndex: 300,
      opacity: pos ? 1 : 0,
      pointerEvents: pos ? "auto" : "none",
      background: "rgba(22, 22, 28, 0.95)",
      backdropFilter: "blur(20px) saturate(1.4)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 14,
      padding,
      boxShadow: "0 16px 48px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.3)",
      width,
      color: "#fff",
      animation: pos ? "fd-popIn 0.15s ease-out" : "none",
    }}>
      {children}
    </div>
  );
}

// ── Popup header ──────────────────────────────────────────────────────
function PopupHeader({ title, onClose }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <span style={{ fontWeight: 700, fontSize: 13, color: "#fff", letterSpacing: "0.3px" }}>{title}</span>
      <button onClick={onClose} style={{
        background: "rgba(255,255,255,0.06)", border: "none", cursor: "pointer",
        color: "#888", padding: 0, width: 24, height: 24, borderRadius: 6,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#888"; }}>
        <Icon id="close" size={12} />
      </button>
    </div>
  );
}

export default function EditorToolChrome({
  fileRef,
  customStickerRef,
  tool,
  color,
  thickness,
  showColorPopup,
  showThickPopup,
  showStickerPanel,
  showAdjust,
  showCrop,
  hasDrawn,
  history,
  redoStack,
  objects,
  selectedIds,
  adjustments,
  setTool,
  setColor,
  setThickness,
  setShowColorPopup,
  setShowThickPopup,
  setShowStickerPanel,
  setShowAdjust,
  setObjects,
  setSelectedIds,
  setAdjustments,
  closeAllPopups,
  rotateImage,
  openCrop,
  resetCrop,
  doneCrop,
  cancelCrop,
  undo,
  redo,
  pushHist,
  placeSticker,
  placeCustomSticker,
  showGridOverlay,
  setShowGridOverlay,
  layoutMode,
}) {
  // Refs for popup anchoring
  const colorBtnRef = useRef(null);
  const thickBtnRef = useRef(null);
  const stickerBtnRef = useRef(null);
  const adjustBtnRef = useRef(null);
  const toolRefs = useRef({});

  const [ww, setWw] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setWw(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const isHorizontal = layoutMode === "auto" ? ww < 1024 : layoutMode === "bottom";

  const tools = [
    { type: "group", label: "Select" },
    { id: "cursor", icon: "cursor", title: "Select (V)", label: "Select" },
    { type: "sep" },
    { type: "group", label: "Draw" },
    { id: "pen", icon: "pen", title: "Freehand Draw", label: "Draw" },
    { id: "line", icon: "line", title: "Line", label: "Line" },
    { id: "arrow", icon: "arrow", title: "Arrow", label: "Arrow" },
    { type: "sep" },
    { type: "group", label: "Shapes" },
    { id: "circle", icon: "circle", title: "Circle", label: "Circle" },
    { id: "rect", icon: "rect", title: "Rectangle", label: "Rect" },
    { id: "blur", icon: "blur", title: "Privacy Blur", label: "Blur" },
    { id: "spotlight", icon: "spotlight", title: "Spotlight Focus", label: "Focus" },
    { type: "sep" },
    { type: "group", label: "Annotate" },
    { id: "text", icon: "text", title: "Text Label", label: "Text" },
    { id: "timestamp", icon: "timestamp", title: "Timestamp", label: "Time" },
  ];

  // ── SIDEBAR (Desktop >= 1024px) ──────────────────────────────────────
  // ── BOTTOM BAR (Mobile / Tablet < 1024px) ────────────────────────────

  const sidebarStyle = isHorizontal ? {
    // Bottom horizontal bar
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "var(--fd-chrome-size, 68px)",
    background: "rgba(18, 18, 22, 0.90)",
    backdropFilter: "blur(20px) saturate(1.8)",
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "0 10px",
    gap: 4,
    zIndex: 20,
    overflowX: "auto",
    overflowY: "hidden",
    boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.4)",
  } : {
    // Vertical sidebar
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "var(--fd-chrome-size, 68px)",
    background: "rgba(18, 18, 22, 0.85)",
    backdropFilter: "blur(20px) saturate(1.8)",
    borderRight: "1px solid rgba(255, 255, 255, 0.04)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 0",
    gap: 2,
    zIndex: 20,
    overflowY: "auto",
    overflowX: "hidden",
    boxShadow: "4px 0 24px rgba(0, 0, 0, 0.3)",
  };

  // Custom scrollbar hide style
  const scrollbarHideClass = isHorizontal ? "fd-hscroll" : "fd-vscroll fd-sidebar-vertical";

  return (
    <>
      <div className={scrollbarHideClass} style={sidebarStyle}>
        {/* ── Tools (Select group) ─────────────── */}
        {tools.slice(0, 2).map((t, i) => {
          if (t.type === "group") {
            if (isHorizontal) return null;
            return <GroupLabel key={`g-${i}`}>{t.label}</GroupLabel>;
          }
          return (
            <Btn key={t.id} onClick={() => { setTool(t.id); closeAllPopups(); }} active={tool === t.id} title={t.title} tooltipDir={isHorizontal ? "top" : "right"}>
              <Icon id={t.icon} size={24} />
            </Btn>
          );
        })}

        {/* ── Color picker button ──────────────── */}
        <Tooltip title="Color" placement={isHorizontal ? "top" : "right"} color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
          <button ref={colorBtnRef}
            onClick={() => { setShowColorPopup(c => !c); setShowThickPopup(false); setShowAdjust(false); setShowStickerPanel(false); }}
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: color,
              border: showColorPopup ? "2px solid #fff" : "2px solid rgba(255,255,255,0.2)",
              cursor: "pointer", margin: isHorizontal ? "0 4px" : "4px 0",
              transition: "all 0.2s", flexShrink: 0,
              boxShadow: showColorPopup ? `0 0 10px ${color}66` : "none",
              transform: showColorPopup ? "scale(1.1)" : "scale(1)",
            }} />
        </Tooltip>

        {/* ── Thickness button ────────────────── */}
        <Tooltip title="Thickness" placement={isHorizontal ? "top" : "right"} color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
          <button ref={thickBtnRef}
            onClick={() => { setShowThickPopup(t => !t); setShowColorPopup(false); setShowAdjust(false); setShowStickerPanel(false); }}
            style={{
              width: 32, height: 32, background: showThickPopup ? "rgba(255,255,255,0.08)" : "transparent",
              border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              margin: isHorizontal ? "0 4px" : "4px 0", borderRadius: 10, flexShrink: 0,
              transition: "all 0.2s",
            }}>
            <div style={{
              width: Math.max(4, thickness * 1.5), height: Math.max(4, thickness * 1.5),
              borderRadius: "50%", background: "#fff",
              boxShadow: showThickPopup ? "0 0 8px rgba(255,255,255,0.3)" : "none",
            }} />
          </button>
        </Tooltip>

        {/* ── Tools (Rest) ──────────────────────── */}
        {tools.slice(2).map((t, i) => {
          if (t.type === "group") {
            if (isHorizontal) return null;
            return <GroupLabel key={`g-${i + 2}`}>{t.label}</GroupLabel>;
          }
          if (t.type === "sep") {
            if (isHorizontal) return <div key={`s-${i + 2}`} style={{ width: 1, height: 28, background: "rgba(255,255,255,0.08)", margin: "0 4px", flexShrink: 0 }} />;
            return <Sep key={`s-${i + 2}`} />;
          }
          return (
            <Btn key={t.id} ref={el => toolRefs.current[t.id] = el} onClick={() => { setTool(t.id); closeAllPopups(); }} active={tool === t.id} title={t.title} tooltipDir={isHorizontal ? "top" : "right"}>
              <Icon id={t.icon} size={20} />
            </Btn>
          );
        })}

        {!isHorizontal && <Sep />}
        {isHorizontal && <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.08)", margin: "0 4px", flexShrink: 0 }} />}

        {/* ── Sticker ─────────────────────────── */}
        <Btn ref={stickerBtnRef}
          onClick={() => { setShowStickerPanel(s => !s); setTool("sticker"); setShowColorPopup(false); setShowThickPopup(false); setShowAdjust(false); }}
          active={tool === "sticker"} title="Stickers" tooltipDir={isHorizontal ? "top" : "right"}>
          <Icon id="sticker" size={24} />
        </Btn>

        {!isHorizontal && <Sep />}
        {isHorizontal && <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.08)", margin: "0 4px", flexShrink: 0 }} />}

        {/* ── Transform tools ─────────────────── */}
        {!isHorizontal && <GroupLabel>Edit</GroupLabel>}
        <Btn onClick={() => { setShowGridOverlay(g => !g); closeAllPopups(); }} active={showGridOverlay} title="Grid Overlay" tooltipDir={isHorizontal ? "top" : "right"}>
          <Icon id="grid" size={isHorizontal ? 22 : 24} />
        </Btn>
        <Btn onClick={rotateImage} active={false} title="Rotate 90°" tooltipDir={isHorizontal ? "top" : "right"}>
          <Icon id="rotate" size={isHorizontal ? 22 : 24} />
        </Btn>
        <Btn onClick={() => { openCrop(); closeAllPopups(); }} active={showCrop} title="Crop Image" tooltipDir={isHorizontal ? "top" : "right"}>
          <Icon id="crop" size={isHorizontal ? 22 : 24} />
        </Btn>
        <Btn ref={adjustBtnRef}
          onClick={() => { setShowAdjust(a => !a); setShowColorPopup(false); setShowThickPopup(false); setShowStickerPanel(false); }}
          active={showAdjust} title="Adjustments" tooltipDir={isHorizontal ? "top" : "right"}>
          <Icon id="adjust" size={isHorizontal ? 22 : 24} />
        </Btn>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ── Floating Popups ──────────────────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      {showColorPopup && (
        <FloatingPopup anchorRef={colorBtnRef} onClose={() => setShowColorPopup(false)} width={172}>
          <PopupHeader title="Color" onClose={() => setShowColorPopup(false)} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {COLORS.map(c => (
              <button key={c} onClick={() => {
                setColor(c);
                if (selectedIds.length) setObjects(os => os.map(o => selectedIds.includes(o.id) ? { ...o, color: c } : o));
                setShowColorPopup(false);
              }}
                style={{
                  width: 30, height: 30, borderRadius: "50%", background: c,
                  border: color === c ? "3px solid #ffcc00" : c === "#ffffff" ? "2px solid rgba(200,200,200,0.4)" : "2px solid transparent",
                  cursor: "pointer",
                  transform: color === c ? "scale(1.18)" : "scale(1)",
                  transition: "all 0.15s ease",
                  boxShadow: color === c ? `0 0 14px ${c}88` : "none",
                  outline: "none",
                }}
                onMouseEnter={e => { if (color !== c) e.currentTarget.style.transform = "scale(1.12)"; }}
                onMouseLeave={e => { if (color !== c) e.currentTarget.style.transform = "scale(1)"; }}
              />
            ))}
            <SketchColorPicker
              color={color}
              size={30}
              onChange={(c) => {
                setColor(c);
                if (selectedIds.length) setObjects(os => os.map(o => selectedIds.includes(o.id) ? { ...o, color: c } : o));
              }}
            />
          </div>
        </FloatingPopup>
      )}

      {showThickPopup && (
        <FloatingPopup anchorRef={thickBtnRef} onClose={() => setShowThickPopup(false)} width={180}>
          <PopupHeader title="Thickness" onClose={() => setShowThickPopup(false)} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", gap: 6 }}>
            {THICKNESSES.map(t => (
              <button key={t} onClick={() => {
                setThickness(t);
                if (selectedIds.length) setObjects(os => os.map(o => selectedIds.includes(o.id) ? { ...o, thickness: t } : o));
                setShowThickPopup(false);
              }}
                style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: thickness === t ? "rgba(255,204,0,0.12)" : "rgba(255,255,255,0.04)",
                  border: thickness === t ? "1.5px solid rgba(255,204,0,0.3)" : "1.5px solid transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s ease", outline: "none",
                }}
                onMouseEnter={e => { if (thickness !== t) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={e => { if (thickness !== t) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                <div style={{
                  width: t * 2.5, height: t * 2.5, borderRadius: "50%",
                  background: thickness === t ? "#ffcc00" : "#fff",
                  boxShadow: thickness === t ? "0 0 10px rgba(255,204,0,0.5)" : "none",
                  transition: "all 0.15s",
                }} />
              </button>
            ))}
          </div>
        </FloatingPopup>
      )}

      {showStickerPanel && (
        <FloatingPopup anchorRef={stickerBtnRef} onClose={() => { setShowStickerPanel(false); setTool("cursor"); }} width={230}>
          <PopupHeader title="Stickers" onClose={() => { setShowStickerPanel(false); setTool("cursor"); }} />

          <button
            onClick={() => customStickerRef.current?.click()}
            style={{
              width: "100%", height: 38, borderRadius: 10,
              border: "1.5px dashed rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.03)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, fontSize: 12, fontWeight: 600, color: "#ccc",
              marginBottom: 12, transition: "all 0.15s", outline: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
          >
            <Icon id="upload" size={14} />
            <span>Upload Custom</span>
          </button>
          <input ref={customStickerRef} type="file" accept="image/*" style={{ display: "none" }} onChange={placeCustomSticker} />

          <div style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 12 }} />

          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8,
            maxHeight: 260, overflowY: "auto", paddingRight: 4,
          }}>
            {STICKERS.map(s => (
              <Tooltip key={s.id} title={s.name} placement="top" color="rgba(15, 15, 18, 0.97)" mouseEnterDelay={0.1}>
                <button
                  onClick={() => placeSticker(s.id)}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    borderRadius: 10, padding: 8, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s", aspectRatio: "1/1", overflow: "hidden",
                    outline: "none",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)"; }}
                >
                  <img crossOrigin="anonymous" src={s.url} alt={s.name}
                    style={{ maxWidth: "85%", maxHeight: "85%", objectFit: "contain" }} />
                </button>
              </Tooltip>
            ))}
          </div>
        </FloatingPopup>
      )}

      {showAdjust && (
        <FloatingPopup anchorRef={adjustBtnRef} onClose={() => setShowAdjust(false)} width={230}>
          <PopupHeader title="Adjustments" onClose={() => setShowAdjust(false)} />
          {[["Brightness", adjustments.brightness, "brightness", 100],
            ["Contrast", adjustments.contrast, "contrast", 100],
            ["Saturation", adjustments.saturation, "saturation", 100]].map(([label, val, key, def]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#ccc", fontWeight: 500 }}>{label}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#888", fontWeight: 600, minWidth: 30, textAlign: "right" }}>{val}%</span>
                  <button onClick={() => setAdjustments(a => ({ ...a, [key]: def }))}
                    style={{
                      fontSize: 10, color: val !== def ? "#ffcc00" : "#555",
                      background: "none", border: "none", cursor: val !== def ? "pointer" : "default",
                      fontWeight: 700, padding: 0, outline: "none",
                    }}>Reset</button>
                </div>
              </div>
              <input type="range" className="fd-dark-slider" min={key === "saturation" ? 0 : 50} max={key === "saturation" ? 200 : 150} value={val}
                onChange={e => setAdjustments(a => ({ ...a, [key]: Number(e.target.value) }))}
                style={{ width: "100%", accentColor: "#ffcc00", height: 4 }} />
            </div>
          ))}
        </FloatingPopup>
      )}

      {/* ── Crop floating bar ──────────────────────────────────────────── */}
      {showCrop && (
        <div style={{
          position: "fixed",
          top: 60,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 300,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          borderRadius: 28,
          padding: "6px 14px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: "#333", paddingLeft: 4, letterSpacing: "0.3px" }}>Crop</span>
          <div style={{ width: 1, height: 18, background: "#ddd" }} />

          <button onClick={resetCrop} title="Reset boundary"
            style={{
              width: 30, height: 30, borderRadius: "50%", border: "none",
              background: "#71717a", color: "#ffffff",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 0.15s", outline: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#52525b"}
            onMouseLeave={e => e.currentTarget.style.background = "#71717a"}>
            <Icon id="undo" size={14} />
          </button>

          <button onClick={cancelCrop} title="Cancel"
            style={{
              width: 30, height: 30, borderRadius: "50%", border: "none",
              background: "#ef4444", color: "#ffffff",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 0.15s", outline: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#dc2626"}
            onMouseLeave={e => e.currentTarget.style.background = "#ef4444"}>
            <Icon id="close" size={14} />
          </button>

          <button onClick={doneCrop} title="Apply Crop"
            style={{
              width: 30, height: 30, borderRadius: "50%", border: "none",
              background: "#22c55e", color: "#ffffff",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 0.15s", outline: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#16a34a"}
            onMouseLeave={e => e.currentTarget.style.background = "#22c55e"}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Active Drawing Tool Banner ─────────────────────────────────── */}
      {!showCrop && hasDrawn && ["pen", "line", "arrow", "circle", "rect", "blur", "spotlight"].includes(tool) && toolRefs.current[tool] && (
        <FloatingPopup anchorRef={{ current: toolRefs.current[tool] }} width={180} padding={12} onClose={() => {}}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Icon id={tools.find(t => t.id === tool)?.icon || "pen"} size={14} color="#ffffff" />
              <span style={{ color: "#ffcc00", fontSize: 12, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                DRAWING MODE
              </span>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, opacity: 0.9 }}>Draw</span>
              <div style={{ display: "flex", alignItems: "center", color: "#ffcc00" }}>
                <Icon id="chevright" size={14} />
              </div>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, opacity: 0.9 }}>Done</span>
              <div style={{ display: "flex", alignItems: "center", color: "#ffcc00" }}>
                <Icon id="chevright" size={14} />
              </div>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, opacity: 0.6 }}>Move</span>
            </div>
            
            <button
              onClick={() => setTool("cursor")}
              style={{
                background: "#ffcc00",
                color: "#000",
                border: "none",
                borderRadius: 6,
                padding: "6px 24px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: "0 2px 8px rgba(255,204,0,0.3)",
                outline: "none",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              Done
            </button>
          </div>
        </FloatingPopup>
      )}
    </>
  );
}
